import React, { useRef, useState, useEffect } from 'react';
import { Image as ImageIcon, Video, X, AlertCircle, Play, Eye } from 'lucide-react';
import { formatMediaUrl, uploadCaseMedia } from '../api/media-api';

/**
 * InlineMediaUploader
 * Header action buttons for "+ Image" and "+ Video" with interactive tooltip and 1-video limit.
 */
export const InlineMediaUploader = ({ section, media = [], onChange }) => {
  const [error, setError] = useState('');
  const [showVideoTooltip, setShowVideoTooltip] = useState(false);
  const [showImageTooltip, setShowImageTooltip] = useState(false);
  const tooltipRef = useRef(null);
  const imageRef = useRef(null);
  const videoRef = useRef(null);

  const sectionItems = media.filter((m) => m.section === section);
  const images = sectionItems.filter((m) => m.type === 'IMAGE');
  const videos = sectionItems.filter((m) => m.type === 'VIDEO');
  const hasVideo = videos.length >= 1;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target)) {
        setShowVideoTooltip(false);
        setShowImageTooltip(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleUpload = async (e, expectedType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setError('');

    if (expectedType === 'IMAGE' && images.length >= 2) {
      setError('Max 2 images per section.');
      return;
    }

    if (expectedType === 'VIDEO') {
      if (videos.length >= 1) {
        setError('Maximum 1 video allowed per section. Remove the existing video to upload a new one.');
        return;
      }

      // Check file size (200MB max)
      if (file.size > 200 * 1024 * 1024) {
        setError('Video must be under 200MB.');
        return;
      }

      // Check format
      const ext = file.name.split('.').pop()?.toLowerCase();
      const isAllowedExt = ['mp4', 'mov', 'webm'].includes(ext);
      const isAllowedMime = ['video/mp4', 'video/quicktime', 'video/webm'].includes(file.type);

      if (!isAllowedExt && !isAllowedMime) {
        setError('Unsupported video format. Allowed formats: MP4 (.mp4), MOV (.mov), WebM (.webm).');
        return;
      }
    }

    if (expectedType === 'IMAGE' && file.size > 10 * 1024 * 1024) {
      setError('Image must be under 10MB.');
      return;
    }

    // Upload starts now, straight to B2, so the transfer overlaps with the vet
    // filling in the rest of the form. The item is rendered immediately from a
    // local blob: preview and its `url` is swapped for the staging key once the
    // upload finishes. Abandoning the form leaves only a tmp/ object, which the
    // bucket's lifecycle rule removes.
    setError('');
    const localUrl = URL.createObjectURL(file);
    const placeholder = {
      url: localUrl,
      previewUrl: localUrl,        // kept so the thumbnail survives the swap
      filename: file.name,
      type: expectedType,
      mimeType: file.type,
      sizeBytes: file.size,
      section,
      order: sectionItems.length,
      uploading: true,
      progress: 0,
    };
    onChange([...media, placeholder]);

    const patch = (changes) =>
      onChange((current) =>
        current.map((m) => (m.previewUrl === localUrl ? { ...m, ...changes } : m))
      );

    try {
      const result = await uploadCaseMedia(file, section, (progress) => patch({ progress }));
      patch({ url: result.url, type: result.type, uploading: false, progress: 100 });
    } catch (err) {
      setError(err?.response?.data?.message || 'Upload failed. Please remove the file and try again.');
      patch({ uploading: false, failed: true });
    }
  };

  const handleVideoClick = () => {
    if (hasVideo) {
      setError('Maximum 1 video allowed per section. Remove the existing video to replace it.');
      setShowVideoTooltip(true);
    } else {
      videoRef.current?.click();
    }
  };

  return (
    <div className="flex flex-col items-end relative" ref={tooltipRef}>
      <div className="flex items-center gap-2">
        <input
          ref={imageRef}
          type="file"
          className="hidden"
          accept="image/jpeg,image/png,image/webp"
          onChange={(e) => handleUpload(e, 'IMAGE')}
        />
        <input
          ref={videoRef}
          type="file"
          className="hidden"
          accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm"
          onChange={(e) => handleUpload(e, 'VIDEO')}
        />

        {/* Upload Image Button with Tooltip Trigger */}
        <div className="relative inline-block">
          <button
            type="button"
            disabled={images.length >= 2}
            onClick={() => imageRef.current?.click()}
            onMouseEnter={() => setShowImageTooltip(true)}
            onMouseLeave={() => setShowImageTooltip(false)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-all shadow-sm border ${
              images.length >= 2
                ? 'bg-gray-100 text-gray-400 border-gray-200'
                : images.length > 0
                ? 'bg-indigo-50/50 text-indigo-800 border-indigo-200 hover:bg-indigo-50'
                : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
            } disabled:opacity-50`}
          >
            <ImageIcon size={13} className={images.length > 0 ? "text-indigo-600" : "text-gray-500"} />
            <span>Image</span>
            {images.length > 0 && <span className="text-[10px] bg-indigo-100 text-indigo-900 px-1.5 py-0.2 rounded-full font-bold">{images.length}/2</span>}
          </button>

          {/* Image Formats & Limits Tooltip Popover */}
          {showImageTooltip && (
            <div className="absolute right-0 top-full mt-2 z-50 w-52 p-2.5 bg-gray-900 text-white rounded-xl shadow-2xl text-xs space-y-1.5 animate-in fade-in zoom-in-95 pointer-events-none border border-gray-700/60">
              <div className="flex items-center gap-1.5 font-bold text-indigo-300 text-xs border-b border-gray-700 pb-1">
                <ImageIcon size={13} />
                <span>Allowed Image Formats</span>
              </div>
              <div className="space-y-1 text-[11px] text-gray-200 pt-0.5">
                <p><span className="font-semibold text-white">• Supported: </span>JPG, JPEG, PNG, WebP</p>
                <p><span className="font-semibold text-white">• Limit: </span>Max 2 images per section</p>
                <p><span className="font-semibold text-white">• Max Size: </span>10 MB per image</p>
              </div>
              <div className="mt-1 pt-1 border-t border-gray-700 text-gray-300 text-[10px] font-medium flex items-center justify-between">
                <span>Attached: {images.length}/2</span>
                {images.length >= 2 && (
                  <span className="text-amber-400 font-semibold">Max reached</span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Upload Video Button with Tooltip Trigger */}
        <div className="relative inline-block">
          <button
            type="button"
            onClick={handleVideoClick}
            onMouseEnter={() => setShowVideoTooltip(true)}
            onMouseLeave={() => setShowVideoTooltip(false)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-all shadow-sm border ${
              hasVideo
                ? 'bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100'
                : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
            } disabled:opacity-50`}
          >
            <Video size={13} className={hasVideo ? 'text-amber-600' : 'text-gray-500'} />
            <span>Video</span>
            {hasVideo && <span className="text-[10px] bg-amber-200 text-amber-900 px-1.5 py-0.2 rounded-full font-bold">1/1</span>}
          </button>

          {/* Video Formats & Limits Tooltip Popover */}
          {showVideoTooltip && (
            <div className="absolute right-0 top-full mt-2 z-50 w-52 p-2.5 bg-gray-900 text-white rounded-xl shadow-2xl text-xs space-y-1.5 animate-in fade-in zoom-in-95 pointer-events-none border border-gray-700/60">
              <div className="flex items-center gap-1.5 font-bold text-amber-300 text-xs border-b border-gray-700 pb-1">
                <Video size={13} />
                <span>Allowed Video Formats</span>
              </div>
              <div className="space-y-1 text-[11px] text-gray-200 pt-0.5">
                <p><span className="font-semibold text-white">• Supported: </span>MP4, MOV, WebM</p>
                <p><span className="font-semibold text-white">• Limit: </span>Max 1 video per section</p>
                <p><span className="font-semibold text-white">• Max Size: </span>200 MB</p>
              </div>
              {hasVideo && (
                <div className="mt-1 pt-1 border-t border-gray-700 text-amber-300 text-[10px] font-medium">
                  ⚠️ 1 video attached. Delete from grid below to replace.
                </div>
              )}
            </div>
          )}
        </div>

      </div>

      {error && (
        <p className="mt-1 text-[11px] text-red-500 flex items-center gap-1 font-medium">
          <AlertCircle size={11} /> {error}
        </p>
      )}
    </div>
  );
};

/**
 * SectionMediaGrid
 * Renders uploaded media cards inside the scrollable form body.
 * Every item is clickable to open full-screen MediaPreviewModal.
 */
export const SectionMediaGrid = ({ section, media = [], onChange, onPreview }) => {
  const sectionItems = media.filter((m) => m.section === section);
  if (sectionItems.length === 0) return null;

  const remove = (item, e) => {
    e.stopPropagation();
    // Release the local preview, otherwise the blob stays alive in the tab for
    // as long as the wizard is open. The uploaded object is left in tmp/ and the
    // bucket's lifecycle rule removes it — no request needed to un-upload.
    const blob = item.previewUrl || (item.url?.startsWith('blob:') ? item.url : null);
    if (blob) URL.revokeObjectURL(blob);
    onChange(media.filter((m) => m !== item));
  };

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
      {sectionItems.map((item, idx) => {
        const isVideo = item.type === 'VIDEO';
        // Prefer the local preview: once the upload finishes, `url` holds a
        // storage key, which is not fetchable. Saved media has no previewUrl and
        // arrives from the API already signed.
        const isBlob = item.url?.startsWith('blob:');
        const displayUrl = item.previewUrl || (isBlob ? item.url : formatMediaUrl(item.url));
        // A draft restored after a page load keeps only the storage key: its
        // blob preview died with the document that made it, and a staged object
        // has no signed URL. Leave the tile empty rather than requesting an
        // address that cannot resolve.
        const src = /^(https?:|blob:|data:)/.test(displayUrl || '') ? displayUrl : undefined;

        return (
          <div
            key={idx}
            onClick={() => onPreview?.(item)}
            className="group relative aspect-video bg-gray-100 rounded-xl overflow-hidden border border-gray-200 shadow-2xs hover:shadow-md transition-all cursor-pointer"
          >
            {!src ? (
              /* Restored from a saved draft: the file is uploaded and its key is
                 held, but the local preview died with the page that made it and
                 a staged object has no signed URL. Name the file so it reads as
                 attached rather than lost. */
              <div className="w-full h-full flex flex-col items-center justify-center gap-1.5 bg-gray-100 text-gray-500 px-2 text-center">
                {isVideo ? <Video size={20} /> : <ImageIcon size={20} />}
                <span className="text-[10px] font-medium leading-tight break-all line-clamp-2">
                  {item.filename || (isVideo ? 'Video' : 'Image')}
                </span>
                <span className="text-[9px] text-gray-400">Attached</span>
              </div>
            ) : isVideo ? (
              <div className="w-full h-full flex flex-col items-center justify-center bg-gray-900 text-white relative">
                <video
                  src={src}
                  className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                  muted
                  preload="metadata"
                />
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center group-hover:bg-black/10 transition-colors">
                  <div className="w-9 h-9 rounded-full bg-white/90 group-hover:bg-white text-gray-900 flex items-center justify-center shadow-lg transition-transform group-hover:scale-110">
                    <Play size={16} className="ml-0.5 text-indigo-600 fill-indigo-600" />
                  </div>
                </div>
                <span className="absolute bottom-1.5 left-1.5 px-2 py-0.5 bg-black/70 backdrop-blur-xs text-[10px] font-medium text-white rounded">
                  VIDEO
                </span>
              </div>
            ) : (
              <div className="w-full h-full relative">
                <img
                  src={src}
                  alt=""
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 flex items-center justify-center transition-colors">
                  <div className="w-8 h-8 rounded-full bg-white/90 text-gray-900 flex items-center justify-center opacity-0 group-hover:opacity-100 shadow transition-opacity">
                    <Eye size={14} className="text-gray-700" />
                  </div>
                </div>
              </div>
            )}

            {/* Upload progress. `progress` was tracked in state but never shown,
                so a large video on a slow connection looked identical to a
                finished one — and Publish refused with no visible reason. */}
            {item.uploading && (
              <div className="absolute inset-0 bg-black/55 flex flex-col items-center justify-center text-white">
                <div className="w-7 h-7 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                <span className="mt-1.5 text-[11px] font-semibold">{item.progress ?? 0}%</span>
              </div>
            )}

            {item.failed && (
              <div className="absolute inset-0 bg-red-900/60 flex flex-col items-center justify-center text-white px-2 text-center">
                <AlertCircle size={18} />
                <span className="mt-1 text-[10px] font-semibold leading-tight">Upload failed</span>
              </div>
            )}

            {/* Remove button */}
            <button
              type="button"
              onClick={(e) => remove(item, e)}
              className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/60 hover:bg-red-600 text-white flex items-center justify-center transition-colors shadow"
              title="Remove media"
            >
              <X size={12} />
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default InlineMediaUploader;
